import * as Sentry from "@sentry/react";

import { createLogger } from "@/logger";

const log = createLogger("bootstrap");

import React, { useEffect } from "react";
import { ErrorBoundary } from "react-error-boundary";
import { ScrollRestoration, useOutlet } from "react-router-dom";

import newDesignCssUrl from "@/assets/new-design-bundle.css?url";
import type { ConfigData, FlagData } from "@/config";
import AppStateProvider, { AppContext } from "@/models/AppStateProvider";
import AuthProvider, { AuthContext } from "@/models/StateProviders/AuthProvider.tsx";
import UserProvider from "@/models/StateProviders/userProvider";
import Layout from "@/routes/layout";
import { type ErrorScreenKind, recordEventAndFlush } from "@/services/telemetry";
import { ChangeEnvironmentUrl } from "./components/ChangeEnvironmentUrl";
import DebugHud from "./components/DebugHud/DebugHud";
import ErrorFallback from "./components/ErrorFallback";
import AutoUpdateApplicationCheck from "./components/electron/AutoUpdateApplicationCheck";
import ElectronComponentLoader from "./components/electron/ElectronComponentLoader";
import ElectronDeepLinkHandler from "./components/electron/ElectronDeepLinkHandler";
import ReloadPrompt from "./components/ReloadPrompt";
import VersionCheckDialog from "./components/VersionCheckDialog";
import { CallProvider } from "./contexts/CallContext";
import ActionsProvider from "./models/ActionsProvider";
import AudioAppContextProvider, { AudioAppContext } from "./models/AudioAppContextProvider";
import DataProvider from "./models/DataProvider";
import LiveQueriesProvider from "./models/LiveQueriesProvider";
import RouteGuard from "./models/RouteGuard";
import SchedulingContextProvider from "./models/SchedulingContextProvider";
import CurrentFeedProvider, { CurrentFeedContext } from "./models/StateProviders/currentFeedProvider";
import MyAccountProvider from "./models/StateProviders/myAccountProvider";
import WebSettingsProvider from "./models/StateProviders/WebSettingsProvider";
import WorkspaceProvider from "./models/StateProviders/workspaceProvider";
import { TelemetryProvider, useTelemetry } from "./models/TelemetryProvider";
import UnreadsContextProvider from "./models/UnreadsContextProvider";
import UppyContextProvider from "./models/UppyContextProvider";
import UxStateProvider, { UxContext } from "./models/UxStateProvider";

function App({ config, flags }: { config: ConfigData; flags: FlagData }) {
  const outlet = useOutlet();
  const { baseDomain, tusUrl, graphqlUrl, graphqlApiKey, sessionRecordingLength, gaKey, env, trackingKey } = config;

  log.debug("AppRoot", {
    baseDomain,
    tusUrl,
    graphqlUrl,
    graphqlApiKey,
    gaKey,
    env,
    trackingKey,
    flags,
  });

  return (
    <>
      <AuthProvider>
        <AppStateProvider
          baseDomain={baseDomain}
          tusUrl={tusUrl}
          graphqlUrl={graphqlUrl}
          graphqlApiKey={graphqlApiKey}
          sessionRecordingLength={sessionRecordingLength}
          initialFlags={flags}
        >
          <TelemetryProviderWrapper>
            <ErrorBoundary
              FallbackComponent={ErrorFallback}
              onError={(error, info) => {
                const kind: ErrorScreenKind = "component";
                Sentry.captureException(error, {
                  tags: { errorScreen: kind },
                  extra: { componentStack: info.componentStack },
                });
                void recordEventAndFlush("error_screen.rendered", {
                  error: { kind, message: error instanceof Error ? error.message : String(error) },
                });
              }}
            >
              <Layout>
                <UserProvider>
                  <AudioAppContextProvider handsFreeModeFlag={true}>
                    <AppContent outlet={outlet} />
                  </AudioAppContextProvider>
                </UserProvider>
              </Layout>
            </ErrorBoundary>
          </TelemetryProviderWrapper>
        </AppStateProvider>
      </AuthProvider>
    </>
  );
}

function TelemetryProviderWrapper({ children }: { children: React.ReactNode }) {
  const { flags } = React.useContext(AppContext);
  const telemetryActive = !flags.inActiveTelemetry;

  return (
    <TelemetryProvider active={telemetryActive}>
      <TelemetryAttributeSetter />
      {children}
    </TelemetryProvider>
  );
}

function TelemetryAttributeSetter() {
  const { setAttribute } = useTelemetry();
  useEffect(() => {
    setAttribute("ui", "new");
  }, [setAttribute]);
  return null;
}

function AudioPlayerFeedGuard() {
  const { currentFeedId } = React.useContext(CurrentFeedContext);
  const { activeItemId, closePlayer } = React.useContext(AudioAppContext);
  const { setFeedViewCollapsed } = React.useContext(UxContext);
  const prevFeedId = React.useRef<string | undefined>(undefined);

  React.useEffect(() => {
    const feedChanged = prevFeedId.current != null && currentFeedId !== prevFeedId.current;
    if (feedChanged && activeItemId) {
      closePlayer();
      setFeedViewCollapsed(false);
    }
    prevFeedId.current = currentFeedId;
  }, [currentFeedId, activeItemId, closePlayer, setFeedViewCollapsed]);

  return null;
}

function AppContent({ outlet }: { outlet: React.ReactElement | null }) {
  const { flags } = React.useContext(AppContext);
  const { user } = React.useContext(AuthContext);

  React.useEffect(() => {
    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = newDesignCssUrl;
    document.head.appendChild(link);

    return () => {
      link.remove();
    };
  }, []);

  return (
    <MyAccountProvider>
      <WebSettingsProvider>
        <WorkspaceProvider>
          <CurrentFeedProvider>
            <UnreadsContextProvider>
              <DataProvider>
                <LiveQueriesProvider>
                  {user?.accounts?.length ? <ElectronComponentLoader component={<ElectronDeepLinkHandler />} /> : null}
                  <UxStateProvider>
                    <AudioPlayerFeedGuard />
                    <ActionsProvider>
                      <RouteGuard>
                        <UppyContextProvider>
                          <SchedulingContextProvider>
                            <CallProvider>
                              {outlet}
                              <ElectronComponentLoader component={<AutoUpdateApplicationCheck />} />
                              <ChangeEnvironmentUrl />
                              <VersionCheckDialog />
                              <ReloadPrompt />
                              <ScrollRestoration />
                              {flags.debugMode && <DebugHud />}
                            </CallProvider>
                          </SchedulingContextProvider>
                        </UppyContextProvider>
                      </RouteGuard>
                    </ActionsProvider>
                  </UxStateProvider>
                </LiveQueriesProvider>
              </DataProvider>
            </UnreadsContextProvider>
          </CurrentFeedProvider>
        </WorkspaceProvider>
      </WebSettingsProvider>
    </MyAccountProvider>
  );
}

// export default App;
export default Sentry.withProfiler(App);
